import { ReactElement } from 'react';
import { isEmpty } from '@client/common/utils/empty/empty';
import { config } from '@client/config';
import { useTranslate } from '@client/i18n/hooks';
import { Price } from '@client/product/components/ProductData/PriceList/Price';
import { ProductDescription } from '@client/product/components/ProductDetails/ProductDescription/ProductDescription';
import { ProductSpecification } from '@client/product/components/ProductDetails/ProductSpecification';
import { useProductGalleryEffect } from '@client/product/hooks';
import { AttributesList } from 'b2b-components/theme//components/AttributesList';
import { FIELD_NAME } from '../PDFForm/PDFFormContainer.types';
import { GalleryImagesType, ImageType, Props } from './PDFContent.types';
import './PDFContent.scss';

export const PDFContent = ({
  values,
  product,
  text,
}: Props): ReactElement => {
  const t = useTranslate();
  const { description, effectivePricing, manufacturer } = product;
  const { bulletPoints, title } = description;
  const cdnUrl = config.url.cdn.images;
  const gallery = useProductGalleryEffect(product.sku);
  const { isLoaded: isGalleryLoaded, data: galleryData } = gallery;
  const isDescriptionAvailable = description
    && (description.subtitle !== ''
      || description.lead !== ''
      || (Array.isArray(description.dangerDetails)
        && description.dangerDetails.length > 0)
      || (Array.isArray(description.dangerIcons)
        && description.dangerIcons.length > 0)
      || (Array.isArray(description.paragraphs)
        && description.paragraphs.length > 0)
      || (Array.isArray(description.banners) && description.banners.length > 0)
      || (Array.isArray(description.remarks) && description.remarks.length > 0));

  const galleryImages = ((): GalleryImagesType | null => {
    if (!isGalleryLoaded || !galleryData || isEmpty(galleryData.images)) {
      return null;
    }

    const fullGallery = galleryData.images.map((img) => {
      const imgUrl = img.sizes.preview || img.sizes.full || img.sizes.thumbnail || '';

      return {
        src: `${cdnUrl}${imgUrl}`,
      };
    });

    if (fullGallery.length > 1) {
      const [main, ...rest] = fullGallery;
      const additional = rest.length < 6 ? rest : rest.filter((el, index) => index < 5);

      return {
        main,
        additional,
      };
    }

    return {
      main: fullGallery[0],
      additional: [],
    };
  })();

  const getFixedPrice = () => {
    if (!values.fixedPrices) {
      return {};
    }

    switch (true) {
      case values.fixedPricesTaxValue.includes(FIELD_NAME.listPrice):
        return {
          value: effectivePricing.listPrice,
          tax: text?.listPrice || t(
            'SRP <strong>incl.</strong> {taxRate} VAT',
            {
              taxRate: `${effectivePricing.priceTaxRate}%`,
            },
            'Suggested Retail Price with VAT',
          ),
        };
      case values.fixedPricesTaxValue.includes(FIELD_NAME.listPriceWithoutVat):
        return {
          value: effectivePricing.listPriceWithoutVat,
          tax: text?.listPriceWithoutVat || t(
            'SRP <strong>excl.</strong> {taxRate} VAT',
            {
              taxRate: `${effectivePricing.priceTaxRate}%`,
            },
            'Suggested Retail Price Without VAT',
          ),
        };
      case values.fixedPricesTaxValue.includes(FIELD_NAME.userPrice): {
        return {
          value: effectivePricing.userPrice,
          tax: t(
            'PP <strong>incl.</strong> {taxRate} VAT',
            {
              taxRate: `${effectivePricing.priceTaxRate}%`,
            },
            'Purchase Price',
          ),
        };
      }
      case values.fixedPricesTaxValue.includes(FIELD_NAME.userPriceWithoutVat): {
        return {
          value: effectivePricing.userPriceWithoutVat,
          tax: t(
            'PP <strong>excl.</strong> {taxRate} VAT',
            {
              taxRate: `${effectivePricing.priceTaxRate}%`,
            },
            'Purchase Price without VAT',
          ),
        };
      }
    }
  };

  const customPrice = values.offer
    ? {
      value: values.offerPriceValue,
      tax: values.offerValue.includes(FIELD_NAME.withVAT)
        ? t('<strong>incl.</strong> {taxRate} VAT', {
          taxRate: `${effectivePricing.priceTaxRate}%`,
        })
        : t('<strong>excl.</strong> {taxRate} VAT', {
          taxRate: `${effectivePricing.priceTaxRate}%`,
        }),
    } : {};
  const mainPrice: any = values.offer ? customPrice : getFixedPrice();
  const secondaryPrice: any = values.offer && values.fixedPrices ? getFixedPrice() : undefined;

  return (
    <div className="PDFContent">
      <div className="PDFContent-Body ProductContent">
        <section className="PDFContent-ContentColumn--Left">
          {values.productName && <h1 className="ProductData-Title">{title}</h1>}

          {!values.offer && !values.fixedPrices
            ? null
            : (
              <section className={'PDFContent-PriceBox'}>
                <div className="PDFContent-PriceBox--PrimaryWrapper">
                  <Price
                    price={mainPrice.value}
                    tax={mainPrice.tax || ''}
                    isSecondary={false}
                  />
                </div>
                {secondaryPrice && (
                  <div className="PDFContent-PriceBox--SecondaryWrapper">
                    <Price
                      className="PDFContent-PriceBox--Secondary"
                      price={secondaryPrice.value}
                      tax={secondaryPrice.tax || ''}
                      isSecondary={true}
                    />
                  </div>
                )}
              </section>
            )
          }

          {values.shortInfo && <AttributesList attributes={bulletPoints} />}

          <table className="ProductData-Features cds-Table cds-Table--Features">
            <tbody>
              {values.sku && (
                <tr>
                  <td>{t('SKU')}:</td>
                  <td>{product.sku}</td>
                </tr>
              )}
              {values.manufacturerNumber && (
                <tr>
                  <td>{t('Manufacturer Product ID')}:</td>
                  <td>{product.manufacturer.productId}</td>
                </tr>
              )}
              {product.ean && (
                <tr>
                  <td>{t('EAN')}:</td>
                  <td>{product.ean}</td>
                </tr>
              )}
              {values.warrantyInfo && (
                <tr>
                  <td>{t('Manufacturer warranty')}:</td>
                  <td className='PDFContent-WarrantyInfo'>
                    {manufacturer.warranty
                      ? t(['month', '{n} months', 'n'], {
                        n: manufacturer.warranty,
                      })
                      : t('0 months')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!!values.additionalComments.length && (
            <p>{values.additionalComments}</p>
          )}
        </section>

        <section className="PDFContent-ContentColumn--Right PDFContent-Gallery">
          {values.mainImage && galleryImages && (
            <div className="PDFContent-ImageWrapper">
              <img
                src={galleryImages.main?.src}
                alt={title}
                className="PDFContent-Image"
              />
            </div>
          )}
          {values.additionalImages && galleryImages
            ? !!galleryImages.additional.length
              && galleryImages.additional.map((img: ImageType, index: number) => (
                <div key={index} className="PDFContent-ImageWrapper">
                  <img src={img.src} alt={title} className="PDFContent-Image" />
                </div>
              ))
            : null}
        </section>

        {values.description && isDescriptionAvailable && (
          <section className="PDFContent-ContentColumn--Left">
            <div className="PDFContent-ProductDataTile">
              <div className="PDFContent-ProductDataTileTitle">
                {t('Description')}
              </div>
              <ProductDescription description={product.description} />
            </div>
          </section>
        )}

        {values.specification && (
          <section className="PDFContent-ContentColumn--Left">
            <div className="PDFContent-ProductDataTile">
              <div className="PDFContent-ProductDataTileTitle">
                {t('Specification')}
              </div>
              <ProductSpecification
                specification={product.description.specification}
                packaging={product.packaging}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
